// Small wrappers around Intl.DateTimeFormat that honour the user's stored
// timezone preference (falling back to the browser's detected zone when the
// user hasn't picked one). Everything here assumes the server sends ISO
// strings with an explicit UTC marker ("Z"); the motor client is configured
// with tz_aware=True to guarantee that.

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Options an ISO-string formatter accepts. `tz` is the preferred user
// timezone; when null/undefined we use the browser's own.
export interface FormatOptions extends Intl.DateTimeFormatOptions {
  tz?: string | null;
}

export function formatInUserTz(
  input: string | number | Date | null | undefined,
  opts: FormatOptions = {}
): string {
  if (input === null || input === undefined || input === '') return '';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);
  const { tz, ...intl } = opts;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz || browserTimezone(),
    ...intl,
  }).format(date);
}

// Common preset — mirrors the shape most existing toLocaleString() sites
// print today (date + time, seconds omitted). Keeping one preset means all
// timestamps look the same across the dashboard.
export function formatDateTime(
  input: string | number | Date | null | undefined,
  tz?: string | null
): string {
  return formatInUserTz(input, {
    tz,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(
  input: string | number | Date | null | undefined,
  tz?: string | null
): string {
  return formatInUserTz(input, {
    tz,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

// Full ISO for tooltip / debug — the actual UTC instant, unformatted.
export function toUtcIso(input: string | number | Date | null | undefined): string {
  if (input === null || input === undefined || input === '') return '';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return String(input);
  return date.toISOString();
}
